'use client'

import React, { useMemo, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Check, ChevronDown } from 'lucide-react'
import { projectOverviewSchema } from '../schemas'
import { projectDomainOptions, resultPresetOptions } from '../constants'

interface ProjectOverviewStepProps {
  formData: {
    resultPreset?: string
    collaborationMode?: 'TEAM' | 'INDIVIDUAL'
    projectDomain: string
    neededRoles: string[]
    projectServiceName: string
    shortSummary: string
    projectType?: string
  }
  onChange: (field: string, value: any) => void
  onNext: () => void
}

const PROJECT_TYPES = [
  { value: 'customer_service', label: '판매/고객 대상 서비스' },
  { value: 'internal', label: '내부 운영 개선' },
  { value: 'mvp', label: 'MVP/검증용 개발' },
  { value: 'other', label: '기타 목적' },
] as const

export const ProjectOverviewStep: React.FC<ProjectOverviewStepProps> = ({ formData, onChange, onNext }) => {
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isProjectDomainOpen, setIsProjectDomainOpen] = useState(false)
  const titleRef = useRef<HTMLInputElement>(null)

  const canProceed = useMemo(() => projectOverviewSchema.safeParse(formData).success, [formData])

  const handleNext = () => {
    const result = projectOverviewSchema.safeParse(formData)
    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors
      const nextErrors: Record<string, string> = {}
      Object.keys(fieldErrors).forEach((key) => {
        const arr = fieldErrors[key as keyof typeof fieldErrors]
        if (arr?.[0]) nextErrors[key] = arr[0]
      })
      setErrors(nextErrors)
      if (nextErrors.projectServiceName && titleRef.current) titleRef.current.focus()
      return
    }
    setErrors({})
    onNext()
  }

  const applyPreset = (presetId: string) => {
    onChange('resultPreset', presetId)

    const preset = resultPresetOptions.find((item) => item.id === presetId)
    if (!preset) return

    onChange('collaborationMode', 'TEAM')
    onChange('projectDomain', preset.domains[0] ?? '')
    onChange('neededRoles', preset.roles)
    onChange('projectServiceName', preset.titleTemplate)
    onChange('shortSummary', preset.summaryTemplate)
    onChange('functionality', preset.descriptionTemplate)
  }

  const selectCustom = () => {
    onChange('resultPreset', 'CUSTOM')
    onChange('projectDomain', 'OTHER')
    onChange('neededRoles', [])
  }

  return (
    <div className="space-y-8">
      <div className="space-y-2 rounded-xl border border-gray-200 bg-gray-50/50 p-4">
        <p className="text-sm font-medium text-gray-900">결과를 빠르게 완성하는 프로듀서 기반 외주</p>
        <p className="text-xs text-gray-600">프로듀서가 제안·협의·계약·정산을 책임지고, 필요하면 팀을 구성해 완수합니다.</p>
        <div className="flex flex-wrap gap-2 pt-2">
          {['안전한 에스크로 결제', '스코프 변경 시 추가 견적 안내', '정산/리마인드 체계적 관리'].map((point) => (
            <span key={point} className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
              ✅ {point}
            </span>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        <div className="space-y-2">
          <Label>추천 템플릿 (빠른 시작) <span className="text-blue-600">*</span></Label>
          <p className="text-xs text-gray-500">자주 요청되는 유형을 빠르게 등록하는 템플릿입니다. 개발/디자인/마케팅 등 모든 분야 가능하며, 없으면 직접 입력을 선택하세요.</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 pt-1">
            {resultPresetOptions.map((preset) => {
              const selected = formData.resultPreset === preset.id
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyPreset(preset.id)}
                  className={`rounded-xl border px-4 py-3 text-left text-sm ${selected ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-700'}`}
                >
                  {preset.title}
                </button>
              )
            })}
          </div>
          <div className="pt-1">
            <button
              type="button"
              onClick={selectCustom}
              className={`text-xs underline underline-offset-2 ${formData.resultPreset === 'CUSTOM' ? 'text-blue-700' : 'text-gray-500'}`}
            >
              ✨ 직접 입력
            </button>
          </div>
          {errors.resultPreset && <p className="text-xs text-red-500">{errors.resultPreset}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="projectServiceName">프로젝트 제목 <span className="text-blue-600">*</span></Label>
          <Input id="projectServiceName" ref={titleRef} value={formData.projectServiceName} onChange={(e) => onChange('projectServiceName', e.target.value)} />
          {errors.projectServiceName && <p className="text-xs text-red-500">{errors.projectServiceName}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="shortSummary">한 줄 요약 <span className="text-blue-600">*</span></Label>
          <Textarea id="shortSummary" maxLength={300} value={formData.shortSummary} onChange={(e) => onChange('shortSummary', e.target.value)} />
          {errors.shortSummary && <p className="text-xs text-red-500">{errors.shortSummary}</p>}
        </div>

        <div className="space-y-2">
          <Label>프로젝트 목적/성격 <span className="text-xs text-gray-500">(선택)</span></Label>
          <div className="grid grid-cols-2 gap-2">
            {PROJECT_TYPES.map((type) => {
              const selected = formData.projectType === type.value
              return (
                <button key={type.value} type="button" onClick={() => onChange('projectType', selected ? undefined : type.value)} className={`relative rounded-xl border px-4 py-3 text-left ${selected ? 'border-blue-200 bg-blue-50' : 'border-gray-200'}`}>
                  {selected && <Check className="absolute right-2 top-2 h-4 w-4 text-blue-600" />}
                  <p className="text-sm">{type.label}</p>
                </button>
              )
            })}
          </div>
        </div>

        {/* 프로젝트 분야(고급 옵션) — 하단 배치, 접기 */}
        <div className="space-y-2 rounded-xl border border-gray-200 p-3">
          <button
            type="button"
            onClick={() => setIsProjectDomainOpen((prev) => !prev)}
            className="flex w-full items-center justify-between text-left"
          >
            <span className="text-sm font-medium text-gray-700">프로젝트 분야 <span className="text-gray-500">(고급 옵션)</span></span>
            <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isProjectDomainOpen ? 'rotate-180' : ''}`} />
          </button>
          {isProjectDomainOpen && (
            <div className="mt-3">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {projectDomainOptions.map((domain) => {
                  const selected = formData.projectDomain === domain.value
                  return (
                    <button
                      key={domain.value}
                      type="button"
                      onClick={() => onChange('projectDomain', domain.value)}
                      className={`rounded-xl border px-3 py-2 text-sm ${selected ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-700'}`}
                    >
                      {domain.label}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
          {errors.projectDomain && <p className="pt-2 text-xs text-red-500">{errors.projectDomain}</p>}
        </div>
      </div>
      <button type="button" onClick={handleNext} disabled={!canProceed} className="hidden">next</button>
    </div>
  )
}
